import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserData, updateUserData } from '../../../redux/middleware/index';
import {
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  Grid,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';

const UserProfile = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.userData);
  const loading = useSelector(state => state.user.loading);
  const error = useSelector(state => state.user.error);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    phoneNumber: '',
    avatar: null,
    country: '',
    city: '',
    postalCode: '',
    street: '',
    countryCode: ''
  });

  const [editMode, setEditMode] = useState({
    avatar: false,
    firstName: false,
    lastName: false,
    gender: false,
    dateOfBirth: false,
    country: false,
    city: false,
    street: false,
    address: false,
    postalCode: false,
    phoneNumber: false
  });

  const phoneInputRef = useRef(null);
  const itiRef = useRef(null);

  useEffect(() => {
    if (!loading && !error) {
      if (!user) {
        dispatch(fetchUserData());
      } else {
        setFormData({
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          username: user.username,
          email: user.email,
          dateOfBirth: user.date_of_birth || '',
          gender: user.gender || '',
          address: user.address || '',
          phoneNumber: user.phone_number || '',
          avatar: user.avata || null,
          country: user.country || '',
          city: user.city || '',
          postalCode: user.postal_code || '',
          street: user.street || '',
          countryCode: user.country_code || ''
        });
      }
    }
  }, [user, dispatch, loading, error]);

  useEffect(() => {
    if (phoneInputRef.current) {
      itiRef.current = intlTelInput(phoneInputRef.current, {
        utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.1.0/build/js/utils.js',
        initialCountry: 'auto',
        separateDialCode: true,
        autoHideDialCode: true,
        preferredCountries: ['KE', 'US']
      });

      phoneInputRef.current.addEventListener('countrychange', () => {
        const countryData = itiRef.current.getSelectedCountryData();
        setFormData(prev => ({
          ...prev,
          countryCode: `+${countryData.dialCode}`
        }));
      });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEditToggle = (field) => {
    setEditMode(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = (field) => {
    dispatch(updateUserData({ [field]: formData[field] }));
    handleEditToggle(field);
  };

  const handleAvatarSubmit = () => {
    if (formData.avatar) {
      const data = new FormData();
      data.append('avatar', formData.avatar);
      dispatch(updateUserData(data));
      handleEditToggle('avatar');
    }
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, avatar: file }));
    }
  };

  const handleGenderChange = (e) => {
    setFormData(prev => ({ ...prev, gender: e.target.value }));
  };

  if (loading) return <p style={{ color: '#E0E0E0' }}>Loading...</p>;
  if (error) return <p style={{ color: '#E0E0E0' }}>Error: {error}</p>;

  return (
    <Box sx={{
      margin: { xs: '0.5rem', md: '1rem' },
      padding: { xs: '1rem 0', md: '2rem 0' },
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: '#121212',
      borderRadius: '16px',
      minHeight: '80vh',
      alignItems: 'center',
      width: '100%'
    }}
    >
      <Box sx={{
        maxWidth: '600px',
        backgroundColor: '#1e1e1e',
        borderRadius: '16px',
        marginTop: '3rem',
        padding: { xs: '1rem', md: '2rem' },
        width: '100%',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
        textAlign: 'center'
      }}
      >
        <Typography variant='h5' fontWeight='bold' color='#E0E0E0' sx={{ marginBottom: '1rem', fontSize: { xs: '1.5rem', md: '2rem' } }}>
          User Profile
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem', position: 'relative' }}>
          <Avatar
            src={formData.avatar instanceof File ? URL.createObjectURL(formData.avatar) : formData.avatar}
            sx={{ width: { xs: 80, md: 100 }, height: { xs: 80, md: 100 } }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginLeft: '1rem' }}>
            {editMode.avatar
              ? (
                <>
                  <IconButton
                    color='primary'
                    component='label'
                    sx={{ marginBottom: '0.5rem' }}
                  >
                    <PhotoCamera />
                    <input hidden accept='image/*' type='file' onChange={handleAvatarUpload} />
                  </IconButton>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={handleAvatarSubmit}
                    sx={{ marginBottom: '0.5rem' }}
                  >
                  Save
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={() => handleEditToggle('avatar')}
                  >
                  Cancel
                  </Button>
                </>
                )
              : (
                <IconButton
                  color='primary'
                  component='label'
                  onClick={() => handleEditToggle('avatar')}
                >
                  <EditIcon sx={{ color: '#E0E0E0' }} />
                </IconButton>
                )}
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ padding: { xs: '0.5rem', md: '1rem' } }}>
          {['firstName', 'lastName', 'country', 'city', 'street', 'address', 'postalCode', 'dateOfBirth', 'gender', 'phoneNumber'].map((field, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', borderBottom: '1px solid #444' }}>
                <Typography color='#E0E0E0' sx={{ fontSize: { xs: '0.85rem', md: '1rem' }, fontWeight: 'bold' }}>
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                </Typography>
                <IconButton onClick={() => handleEditToggle(field)}>
                  <EditIcon sx={{ color: editMode[field] ? 'gray' : '#E0E0E0' }} />
                </IconButton>
              </Box>
              {editMode[field]
                ? (
                  <>
                    {field === 'gender'
                        ? (
                            <FormControl fullWidth variant='outlined' sx={{ marginTop: '0.5rem' }}>
                                <InputLabel id='gender-label'>Gender</InputLabel>
                                <Select
                                    labelId='gender-label'
                                    name='gender'
                                    value={formData.gender}
                                    onChange={handleGenderChange}
                                    label='Gender'
                                  >
                                    <MenuItem value='male'>Male</MenuItem>
                                    <MenuItem value='female'>Female</MenuItem>
                                    <MenuItem value='other'>Other</MenuItem>
                                  </Select>
                              </FormControl>
                          )
                        : (
                            <TextField
                                name={field}
                                value={formData[field]}
                                onChange={handleChange}
                                variant='outlined'
                                fullWidth
                                type={field === 'dateOfBirth' ? 'date' : 'text'}
                                sx={{
                                    backgroundColor: '#000',
                                    marginTop: '0.5rem',
                                    borderRadius: '4px',
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': {
                                        borderColor: 'gray'
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#E0E0E0'
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#E0E0E0'
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      color: '#E0E0E0'
                                    },
                                  }}
                              />
                          )}
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={() => handleSave(field)}
                        sx={{ marginTop: '0.5rem' }}
                      >
                                      Save
                      </Button>
                    <Button
                        variant='outlined'
                        onClick={() => handleEditToggle(field)}
                        sx={{ marginLeft: '0.5rem', marginTop: '0.5rem' }}
                      >
                                      Cancel
                      </Button>
                  </>
                  )
                : (
                  <Typography color='#E0E0E0' sx={{ marginTop: '0.5rem' }}>
                    {formData[field]}
                  </Typography>
                  )}
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default UserProfile;
